<template>
  <v-flex xs12>
    <v-card>
      <v-progress-linear :indeterminate="true" v-if="generateLoading" class="loading"/>
      <v-flex xs12 px-4 py-3 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-flex xs12 px-4 py-3 v-if="success">
        <v-alert :value="true" type="success">{{success}}</v-alert>
      </v-flex>
      <v-form v-model="valid" class="pt-3" v-if="!generate">
        <v-flex xs12 sm7 md5 lg4 px-4>
          <v-select
            :label="$lang.header.licenseType"
            v-model="licenseType"
            :items="types"
            :rules="[v => !!v || `${$lang.errors.missingLicenseType}`]"
            required
          />
        </v-flex>
        <template v-if="licenseType === 'daily'">
          <v-flex xs12 sm7 md5 lg4 px-4>
            <v-menu
              ref="menu"
              v-model="menu"
              :return-value.sync="fromDate"
              lazy
              transition="scale-transition"
              full-width
              min-width="290px"
            >
              <v-text-field
                slot="activator"
                v-model="fromDate"
                :label="$lang.labels.fromDate"
                prepend-icon="event"
                required
                :rules="[v => !!v || `${$lang.errors.missingFromDate}`]"
                readonly
              />
              <v-date-picker color="primary" v-model="fromDate" no-title scrollable  @input="$refs.menu.save(fromDate)" />
            </v-menu> 
            <v-menu
              ref="menu2"
              v-model="menu2"
              :return-value.sync="toDate"
              lazy
              transition="scale-transition"
              full-width
              min-width="290px"
            >
              <v-text-field
                slot="activator"
                v-model="toDate"
                :label="$lang.labels.toDate"
                prepend-icon="event"
                required
                :rules="[v => !!v || `${$lang.errors.missingToDate}`]"
                readonly
              />
              <v-date-picker color="primary" v-model="toDate" no-title scrollable  @input="$refs.menu2.save(toDate)" />
            </v-menu>
            <v-autocomplete
              class="min-w max-w mx-2 mb-4"
              :label="$lang.actions.selectCompany"
              :items="companies"
              :loading="companyLoading"
              v-model="selectedCompany"
              item-text="name"
              item-value="id"
              hide-details
              clearable
            />
          </v-flex>
        </template>
        <template v-if="licenseType === 'monthly' || licenseType === 'monthlyaggregated'">
          <v-layout row wrap class="px-2">
            <v-flex xs12 sm7 md5 lg4 px-3>
              <v-select
                :items="years"
                :label="$lang.actions.selectYear"
                v-model="year"
                required
                :rules="[v => !!v || `${$lang.errors.missingYear}`]"              
              />
            </v-flex>
            <v-flex xs12 sm7 md5 lg4 px-3>
              <v-select
                :items="months"
                :label="$lang.actions.selectMonth"
                v-model="month"
                required
                :rules="[v => !!v || `${$lang.errors.missingMonth}`]"
              />
            </v-flex>
          </v-layout>
        </template>
        <v-layout row wrap align-center justify-space-between class="px-4 pb-3">
          <div>
            <v-btn class="primary ml-0" :disabled="!valid" @click="showReports">{{$lang.actions.showReports}}</v-btn>
            <v-btn class="primary mx-0" @click="showGenerate()">{{$lang.actions.generateMonthly}}</v-btn>
          </div>
          <v-text-field
            v-if="reports.length !== 0"
            class="min-w max-w"
            v-model="search"
            append-icon="search"
            :label="$lang.labels.search"
            hide-details />
        </v-layout>
      </v-form>
      <v-form class="pt-3" v-model="generateValid" v-if="generate">
        <v-flex xs12 sm7 md5 lg4 px-4>
          <v-select
            :items="years"
            :label="$lang.actions.selectYear"
            v-model="year"
            required
            :rules="[v => !!v || `${$lang.errors.missingYear}`]"              
          />
          <v-select
            :items="months"
            :label="$lang.actions.selectMonth"
            v-model="month"
            required
            :rules="[v => !!v || `${$lang.errors.missingMonth}`]"
          />
        </v-flex>
        <v-layout row wrap align-center class="pb-4">
          <v-btn class="primary mx-4" @click="generateReport()" :disabled="!generateValid || generateLoading">{{$lang.actions.generate}}</v-btn>
          <v-btn class="mx-4" flat @click="close()">{{$lang.actions.close}}</v-btn>
        </v-layout>
      </v-form>
      <v-data-table
        v-if="show && !generate"
        :headers="(licenseType === 'monthlyaggregated' ? aggrHeaders : headers)"
        :items="reports"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="[50, 100, 250, 500, { text:'All' , value: -1 }]"
      >
        <v-progress-linear height="6" slot="progress" color="primary" indeterminate />
        <template slot="items" slot-scope="props">
          <td v-if="licenseType !== 'monthlyaggregated'">{{ props.item.companyName }}</td>
          <td>{{ props.item.type }}</td>
          <td v-if="licenseType === 'monthlyaggregated'">{{ props.item.year }}</td>
          <td v-if="licenseType === 'monthlyaggregated'">{{ months[parseInt(props.item.month, 10) - 1].text }}</td>
          <td>{{ formatDate(props.item.createdOn) }}</td>
          <td>
            <v-btn 
              class="primary mx-0 elevation-1" 
              @click="viewLicense(props.item)">
              {{$lang.actions.view}}
            </v-btn>
          </td>
        </template>
        <v-alert slot="no-results" :value="true" type="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card>
    <v-dialog v-if="licenseReportDetailsModal" v-model="licenseReportDetailsModal" max-width="1880">
      <license-report-details-modal :licenseReportDetails="licenseReportDetails" @close="licenseReportDetailsModal = false"/>
    </v-dialog>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      licenseReportDetailsModal: () => import('../modals/LicenseReportDetailsModal'),
    },
    data() {
      return {
        err: '',
        success: '',
        search: '',
        show: false,
        loading: true,
        valid: false,
        generateValid: false,
        generate: false,
        reports: [],
        companies: [],
        selectedCompany: '',
        companyLoading: true,
        headers: [
          { text: this.$lang.labels.company, value: 'companyName', align: 'left' },
          { text: this.$lang.labels.type, value: 'type', align: 'left', sortable: false },
          { text: this.$lang.labels.created, value: 'createdOn', align: 'left' },
          { text: this.$lang.labels.actions, value: 'actions', align: 'left', sortable: false },
        ],
        aggrHeaders: [
          {text: this.$lang.labels.type, value: 'type', align: 'left', sortable: false },
          {text: this.$lang.labels.year, value: 'year', align: 'left' },
          {text: this.$lang.labels.month, value: 'month', align: 'left' },
          {text: this.$lang.labels.created, value: 'createdOn', align: 'left' },
          {text: this.$lang.labels.actions, value: 'actions', align: 'left', sortable: false },
        ],
        types: [
          {text: this.$lang.labels.daily, value: 'daily'}, 
          {text: this.$lang.labels.monthly, value: 'monthly'}, 
          {text: this.$lang.labels.monthlyAgg, value: 'monthlyaggregated'},
        ],
        pagination: {
          sortBy: 'createdOn', 
          descending: true,
        },
        menu: false,
        menu2: false,
        fromDate: '',
        toDate: '',
        licenseType: '',
        year: DateTime.local().year,
        month: DateTime.local().month,
        months: [
          {value: 1, text: this.$lang.labels.january},
          {value: 2, text: this.$lang.labels.february},
          {value: 3, text: this.$lang.labels.march},
          {value: 4, text: this.$lang.labels.april},
          {value: 5, text: this.$lang.labels.may},
          {value: 6, text: this.$lang.labels.june},
          {value: 7, text: this.$lang.labels.july},
          {value: 8, text: this.$lang.labels.august},
          {value: 9, text: this.$lang.labels.september},
          {value: 10, text: this.$lang.labels.october},
          {value: 11, text: this.$lang.labels.november},
          {value: 12, text: this.$lang.labels.december},
        ],
        generateLoading: false,
        licenseReportDetailsModal: false,
        licenseReportDetails: null,
      };
    },
    watch: {
      licenseType(newVal, oldVal) {
        if (newVal) {
          this.fromDate = DateTime.local().plus({ days: -1 }).toFormat('yyyy-MM-dd');
          this.toDate = DateTime.local().toFormat('yyyy-MM-dd');
          this.year = DateTime.local().year;
          this.month = DateTime.local().month;
        }
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
    },
    computed: {
      years() {
        let array = [];
        let x = 2018;
        for (x; x <= DateTime.local().year; x++) {
          array.push(x);
        }
        return array;
      },
    },
    methods: {
      showGenerate() {
        this.generate = true;
      },
      close() {
        this.year = DateTime.local().year;
        this.month = DateTime.local().month;
        this.generate = false;
      },

      formatDateForSubmit(date) {
        const correctDate = new Date(date);
        correctDate.setUTCHours(0, 0, 0, 0);
        return correctDate.toISOString();
      },
      generateReport() {
        this.generateLoading = true;
        const data = {
          month: this.month,
          year: this.year
        };
        this.$http.post('licenseusage/generatemonthlyaggregate', data)
          .then((res) => {
            this.generateLoading = false;
            if (res.body.status === 'Success') {
              this.success = 'License Report generated successfuly';
              setTimeout(() => {
                this.success = '';
              }, 5000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.generateLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm');
      },
      viewLicense(license) {
        this.licenseReportDetails = {
          id: license.id, 
          type: license.type,
        }
        this.licenseReportDetailsModal = true;
      },
      showReports() {
        this.show = true;
        this.loading = true;
        this.reports = [];
        if (this.licenseType === 'daily') {
          const data = {
            type: this.licenseType,
            fromDate: this.formatDateForSubmit(this.fromDate),
            toDate: this.formatDateForSubmit(this.toDate),
          };
          if (this.selectedCompany) {
            Object.assign(data, { companyId: this.selectedCompany })
          }
          this.$http.post('licenseusage/getall', data)
            .then((res) => {
              if (res.body.status !== 'Success') {
                this.err = res.body.statusText;
                return;
              } 
                this.reports = res.body.licencesUsages;
                if (this.reports !== 0) {
                  this.loading = false;
                }
              }, (err) => {
                this.show = false;
                this.err = err.body.statusText || err.body.message;
              });
        }
        if (this.licenseType === 'monthly' || this.licenseType === 'monthlyaggregated') {
          const data = {
            month: this.month,
            type: this.licenseType,
            year: this.year,
          };
          this.$http.post('licenseusage/getforyearandmonth', data)
            .then((res) => {
              if (res.body.status !== 'Success') {
                this.err = res.body.statusText;
                return;
              }
                this.reports = res.body.licencesUsages;
                if (this.reports !== 0) {
                  this.loading = false;
                }
              }, (err) => {
                this.show = false;
                this.err = err.body.statusText || err.body.message;
              });
        }
      },
    },
    created() {
      this.$http.get('company')
        .then((res) => {
          this.companyLoading = false;
          if (res.body.status === 'Success') {
            this.companies = res.body.companies;
            return;
          }
          this.err = res.body.statusText;
        }, (err) => {
          this.companyLoading = false;
          this.err = err.body.statusText || err.body.message;
        });
    },
  };
</script>

<style lang="scss" scoped>
  .loading {
    position: absolute;
    top: 0;
    margin: 0;
  }
</style>



// WEBPACK FOOTER //
// LicenseReports.vue?6521f697